import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from "../../../../store/notifications/notifications.actions";
import { Permission, PermittedCommand } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { LOCALSTORAGE_PIN_KEY_PREFIX } from 'src/app/conf/permissions';
import { ActivatedRoute } from '@angular/router';
import { selectCommandExecutionError, selectCommandExecutionErrorId, selectPermission, selectShowPinPad, selectValidPin, selectValidToken } from 'src/app/store/permissions/permissions.selector';
import { AppComponent } from 'src/app/app.component';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { selectCurrentLang } from 'src/app/store/user/user.selector';

@Component({
	selector: 'app-send-command',
	templateUrl: './send-command.component.html',
})

export class SendCommandComponent implements OnInit, OnDestroy {
	// Store selectors
	validToken$: Observable<boolean | undefined>;
	validTokenSub: Subscription | undefined;
	validPin$: Observable<boolean | undefined>;
	validPinSub: Subscription | undefined;
	showPinPad$: Observable<boolean | undefined>;
	showPinPadSub: Subscription | undefined;
	permission$: Observable<Permission | undefined>;
	permissionSub: Subscription | undefined;
	commandExecutionError$: Observable<string | undefined>;
	commandExecutionErrorSub: Subscription | undefined;
	commandExecutionErrorId$: Observable<number | undefined>;
	commandExecutionErrorIdSub: Subscription | undefined;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;

	token = '';
	validToken = true;

	pin = '';
	validPin: boolean;

	permission: Permission;

	commandExecutionError: string;
	commandExecutionErrorId: number;

	commandListSize: number;
	columnNumber: string;

	showPinPad: boolean;

	automationList: Map<string, PermittedCommand[]>;

	resetPin = false;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MAX_MD_WIDTH = 1120;
	DELTA_ERROR_PIXEL = 7;
	MIN_TWO_BUTTONS_DIALOG_WIDTH = 575 + this.DELTA_ERROR_PIXEL;

	constructor(
		private store: Store<AppState>,
		private route: ActivatedRoute,
		public app: AppComponent,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.validToken$ = this.store.select(selectValidToken);
		this.validPin$ = this.store.select(selectValidPin);
		this.showPinPad$ = this.store.select(selectShowPinPad);
		this.permission$ = this.store.select(selectPermission);
		this.commandExecutionError$ = this.store.select(selectCommandExecutionError);
		this.commandExecutionErrorId$ = this.store.select(selectCommandExecutionErrorId);
	}

	ngOnInit() {
		this.store.dispatch(NotificationsActions.setTopbarTitleId({
			stringId: 'QUICK_EASY'
		}));
		this.token = this.route.snapshot.params['token'];
		this.pin = localStorage.getItem(`${LOCALSTORAGE_PIN_KEY_PREFIX}${this.token}`);
		if (this.pin) {
			this.sendCommand(new PermittedCommand());
		}
		this.store.dispatch(PermissionActions.getTokenPermissionCommands({ token: this.token }));
		this.validTokenSub = this.validToken$.subscribe(valid => {
			this.validToken = valid;
		});
		this.validPinSub = this.validPin$.subscribe(valid => {
			this.validPin = valid;
		});
		this.showPinPadSub = this.showPinPad$.subscribe(show => {
			this.showPinPad = show;
		});
		this.permissionSub = this.permission$.subscribe(permission => {
			this.permission = permission;
			this.automationList = new Map<string, PermittedCommand[]>();
			if (this.permission !== undefined) {
				for (const command of this.permission.Commands) {
					if (!this.automationList.has(command.DeviceName.toUpperCase())) {
						const list = [];
						list.push(command);
						this.automationList.set(command.DeviceName.toUpperCase(), list);
						this.commandListSize = list.length;
					} else {
						this.automationList.get(command.DeviceName.toUpperCase()).push(command);
					}
				}
				this.automationList.forEach((value: PermittedCommand[]) => {
					value.sort((a, b) => {
						if (a.CommandDescription.toLowerCase() < b.CommandDescription.toLowerCase()) //sort string ascending
							return -1
						return 1
					});
				});
			}
		});

		this.commandExecutionErrorSub = this.commandExecutionError$.subscribe(commandExecutionError => {
			this.commandExecutionError = commandExecutionError;
		});

		this.commandExecutionErrorIdSub = this.commandExecutionErrorId$.subscribe(commandExecutionErrorId => {
			if (commandExecutionErrorId === null) {
				this.commandExecutionErrorId = 0;
			} else {
				this.commandExecutionErrorId = commandExecutionErrorId;
			}
		});
		this.columnNumber = this.getColumnNumber();

		this.currentLang$ = this.store.select(selectCurrentLang);
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});

		this.onResize();
	}

	ngOnDestroy(): void {
		this.validTokenSub?.unsubscribe();
		this.validPinSub?.unsubscribe();
		this.showPinPadSub?.unsubscribe();
	}

	onPinInserted(event: number[]) {
		this.pin = event.join('');
		this.sendCommand(new PermittedCommand());
	}

	sendCommand(cmd: PermittedCommand) {
		this.store.dispatch(PermissionActions.executeTokenCommand({
			token: this.token,
			pin: this.pin,
			command: cmd,
		}));
	}

	resetPinToken() {
		this.store.dispatch(PermissionActions.resetPinToken({
			token: this.token,
		}));
		this.resetPin = true;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onMobile(): boolean {
		return this.screenWidth <= this.MIN_MD_WIDTH;
	}

	dynamicButtonWidth(): string {
		return this.onMobile()
			? 'w-full'
			: 'w-24rem';
	}

	getColumnNumber(): string {
		if (this.commandListSize === 2) {
			return 'col-6';
		} else if (this.commandListSize >= 3) {
			return 'col-4';
		} else {
			return 'col-12';
		}
	}
}